/**
 * UI Store - Zustand store for application-wide UI state.
 * 
 * Manages:
 * - Navigation view ('current', 'history', etc.)
 * - Drawer open/close state
 * - Dark mode preference
 * - Toast messages
 * - Modals (Upgrade, etc.)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ViewType, UIState } from '../types';

interface UIActions {
    setView: (view: ViewType) => void;
    setDrawerOpen: (isOpen: boolean) => void;
    toggleDrawer: () => void;
    setDarkMode: (isDark: boolean) => void;
    toggleDarkMode: () => void;
    triggerToast: (msg: string | null, duration?: number) => void;
    setFadingId: (id: string | null) => void;
    setUpgradeModal: (isOpen: boolean, feature?: string) => void;
    setActionLoading: (id: string | null) => void;
    setFadingInventoryItem: (name: string | null) => void;
    reset: () => void;
}

export type UIStore = UIState & UIActions;

const initialState: UIState = {
    view: 'current',
    isDrawerOpen: false,
    isDarkMode: false,
    showToast: null,
    fadingId: null,
    isUpgradeModalOpen: false,
    upgradeFeature: '',
    actionLoading: null,
    fadingInventoryItem: null,
};

export const useUIStore = create<UIStore>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,

                setView: (view) => set({ view }),
                setDrawerOpen: (isDrawerOpen) => set({ isDrawerOpen }),
                toggleDrawer: () => set((state) => ({ isDrawerOpen: !state.isDrawerOpen })),
                setDarkMode: (isDarkMode) => set({ isDarkMode }),
                toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),

                triggerToast: (msg, duration = 3000) => {
                    set({ showToast: msg });
                    if (msg) {
                        setTimeout(() => {
                            set((state) => (state.showToast === msg ? { showToast: null } : {}));
                        }, duration);
                    }
                },

                setFadingId: (fadingId) => set({ fadingId }),
                setUpgradeModal: (isUpgradeModalOpen, upgradeFeature = '') =>
                    set({ isUpgradeModalOpen, upgradeFeature }),
                setActionLoading: (actionLoading) => set({ actionLoading }),
                setFadingInventoryItem: (fadingInventoryItem) => set({ fadingInventoryItem }),

                reset: () => set(initialState),
            }),
            {
                name: 'ui-storage',
                partialize: (state) => ({ isDarkMode: state.isDarkMode }), // Persist only preferences
            }
        ),
        { name: 'ui-store' }
    )
);
